<template>
  <div class="notifications-list h-full flex flex-col p-4">
    <!-- Category filters (if enabled) -->
    <div
      v-if="enableFiltering && availableCategories.length > 1"
      class="filters mb-3 flex flex-wrap gap-2"
    >
      <button
        class="text-xs px-3 py-1 rounded-full transition-colors"
        :class="[
          activeFilter === null
            ? 'bg-primary text-white'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
        ]"
        @click="setFilter(null)"
      >
        All
      </button>
      <button
        v-for="category in availableCategories"
        :key="category"
        class="text-xs px-3 py-1 rounded-full transition-colors"
        :class="[
          activeFilter === category
            ? 'bg-primary text-white'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
        ]"
        @click="setFilter(category)"
      >
        {{ category }}
      </button>
    </div>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="py-8 text-center"
    >
      <div class="spinner inline-block w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      <p class="text-gray-500 mt-2">
        Loading notifications...
      </p>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="filteredNotifications.length === 0"
      class="py-8 text-center"
    >
      <div class="text-gray-400 mb-2">
        <i class="fas fa-bell-slash text-2xl" />
      </div>
      <p class="text-gray-500">
        {{ activeFilter ? `No ${activeFilter} notifications` : 'No notifications to display' }}
      </p>
      <button
        v-if="activeFilter"
        class="mt-2 text-sm text-primary hover:underline"
        @click="setFilter(null)"
      >
        View all notifications
      </button>
    </div>

    <!-- Notifications list -->
    <div
      v-else
      ref="containerRef"
      :style="{ height: props.maxHeight === '100%' ? '100%' : 'auto', maxHeight: props.maxHeight === '100%' ? 'none' : (props.maxHeight || '400px') }"
      class="overflow-y-auto px-1 flex-1"
      @scroll="handleScroll"
    >
      <NotificationsNotificationItem
        v-for="notification in notifications"
        :key="notification.id"
        :notification="notification"
        :compact="compact"
        @notification-click="(notification) => {
          $emit('notificationClick', notification);
        }"
        @mark-as-read="(id) => markAsRead(id)"
        @delete="(id) => deleteNotification(id)"
      />

      <!-- Loading more indicator -->
      <div
        v-if="loadingMore"
        class="text-center py-2"
      >
        <div class="spinner inline-block w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        <span class="text-sm text-gray-500 ml-2">Loading more...</span>
      </div>

      <!-- End of list indicator -->
      <div
        v-else-if="!hasMore && filteredNotifications.length > 0"
        class="text-center py-2 text-sm text-gray-500"
      >
        No more notifications
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue' // Added computed import
import { useNotifications } from '~/composables/useNotifications'
import type { Notification } from '~/shared/types/notification'

const props = defineProps<{
  maxHeight?: string
  compact?: boolean
  enableFiltering?: boolean // New prop to enable category filtering
}>()

defineEmits<{
  notificationClick: [notification: Notification]
}>()

// Remove unreadCount from destructuring since we don't use it
const {
  notificationsArray,
  loadNotifications,
  loadMoreNotifications,
  markAsRead,
  deleteNotification,
  hasMore,
  loadingMore,
} = useNotifications()

const notifications = ref<Notification[]>([])
const loading = ref(true)
const containerRef = ref<HTMLElement | null>(null)
const activeFilter = ref<string | null>(null) // For category filtering

// Load initial notifications
onMounted(async () => {
  loading.value = true
  const initialNotifications = await loadNotifications()
  notifications.value = initialNotifications
  loading.value = false
})

// Implement infinite scroll
const handleScroll = async (event: Event) => {
  if (!hasMore.value || loadingMore.value) return

  const container = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = container

  // Load more when scrolled to bottom (with a 50px threshold)
  if (scrollHeight - scrollTop - clientHeight < 50) {
    const moreNotifications = await loadMoreNotifications()
    if (moreNotifications.length > 0) {
      notifications.value = [...notifications.value, ...moreNotifications]
    }
  }
}

// Filter notifications by category
const filteredNotifications = computed(() => {
  if (!activeFilter.value) return notifications.value
  return notifications.value.filter(notif => notif.category === activeFilter.value)
})

// Set active filter
const setFilter = (category: string | null) => {
  activeFilter.value = category
}

// Get unique categories from notifications
const availableCategories = computed(() => {
  const categories = new Set<string>()
  notifications.value.forEach((notif) => {
    if (notif.category) categories.add(notif.category)
  })
  return Array.from(categories)
})

// Watch for changes in notificationsArray from the store
watch(notificationsArray, (newNotifications) => {
  if (newNotifications) {
    notifications.value = newNotifications
  }
})
</script>

<style scoped>
.spinner {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
